import Play from "./play";
import FootballGameContext from "../context/football-game-context";
import FootballState from "../state/football-state";
import FootballPlayState from "../state/football-play-state";
import FootballTransition from "../state/football-transition";
import SportCodes from "../../common/sport-codes";
import { isArray } from "../../../services/common/object-util";
import KickoffSkip from "../enums/kickoff-skip";
import DefaultFootballConfig from "../config/default";
import FootballPlayEndContextSnapshot from "../context/football-play-end-context-snapshot";
import CollectDriveTime from "../fragments/collect-drive-time";

export default class FootballPlay extends Play {
    constructor(
        stateMachine = new FootballPlayState(),
        contextSnapshot = new FootballGameContext().generateSnapshot()
    ) {
        super(stateMachine);
        this.driveId = null;
        this.contextSnapshot = contextSnapshot;
    }

    fromJSON(obj) {
        FootballPlay._healStaleSnapshotClock(obj);
        super.fromJSON(obj);
    }

    /**
     * Defensive fallback: freeze a running gameClock captured inside a
     * contextSnapshot. Normally freezeClock() in FootballGameContextSnapshot
     * already resolves the clock at snapshot-creation time, so this should
     * never fire. It exists as a safety net for snapshots that bypassed
     * freezeClock (e.g. direct construction or older code paths).
     *
     * Uses play.time (recorded at snapshot-save) as the authoritative
     * game-clock value — more accurate than the raw referenceValue.
     */
    static _healStaleSnapshotClock(obj) {
        const clock =
            obj && obj.contextSnapshot && obj.contextSnapshot.gameClock;
        if (clock && clock.referenceTime != null && clock.paused === false) {
            clock.referenceValue =
                typeof obj.time === "number" ? obj.time : clock.referenceValue;
            clock.referenceTime = null;
            clock.paused = true;
        }
    }

    getSportCode() {
        return SportCodes.Football;
    }

    isComplete() {
        return this.fsm.state === FootballState.Finalize;
    }

    getLastFragment(state) {
        if (state) {
            return super.getLastFragment(state);
        }
        return this.fragments.length
            ? this.fragments[this.fragments.length - 1]
            : null;
    }

    /**
     *
     * @param {*} fragment Initial Fragment
     * @param {String[] | string} states State(s)
     * @returns All fragments between the provided Initial Fragment and the fragment from the first occurance of the provided State(s) or to the last fragment if no occurance exists.
     */
    getFragmentsBetween(fragment, states) {
        const { associatedHistoryIndex: startIndex } = fragment;
        const { associatedHistoryIndex: endIndex } =
            this.fragments.find(
                ({ associatedHistoryIndex: idx, associatedState }) =>
                    idx > startIndex &&
                    (isArray(states)
                        ? states.some((s) => s === associatedState)
                        : states === associatedState)
            ) || {};

        return this.fragments.slice(startIndex - 1, endIndex);
    }

    /**
     *
     * @param {*} fragment Initial Fragment
     * @returns All fragments that preceed the provided Initial Fragment
     */
    getPreceedingFragments(fragment) {
        const { associatedHistoryIndex: startIndex } = fragment || {};
        if (startIndex) {
            const { associatedHistoryIndex: historyBeginIndex } =
                this.getFirstFragment(FootballState.AddPlay);
            return this.fragments.filter(
                ({ associatedHistoryIndex: idx }) =>
                    idx < startIndex && idx > historyBeginIndex
            );
        }
        return this.fragments;
    }

    /**
     *
     * @param {*} fragment Initial Fragment
     * @param {string[] | string} states State(s)
     * @returns Fragment from the first occurance of the provided State(s) that preceed the Initial Fragment
     */
    getPreceedingFragment(fragment, states) {
        const f = this.getPreceedingFragments(fragment)
            .sort((a, b) => b.associatedHistoryIndex - a.associatedHistoryIndex)
            .find(({ associatedState }) =>
                isArray(states)
                    ? states.some((state) => state === associatedState)
                    : associatedState === states
            );

        return f;
    }

    /**
     * @param {string[]} excludeStates States whose field position is ignored
     * @param {string[]} stopAtStates States the play is measured up to: the
     * first one found ends the search, so spots after it do not move this
     * measurement. What happens after still counts, under its own stat --
     * a fumble recovery and its advance are fumble-return yards for the
     * recovering player, never yards for the passer or rusher.
     * @returns {number} The last field position of the play
     */
    getEndFieldPosition(excludeStates = [], stopAtStates = []) {
        const stopIndex = this.fragments.findIndex((f) =>
            stopAtStates.includes(f.associatedState)
        );

        const fragments =
            stopIndex === -1
                ? this.fragments
                : this.fragments.slice(0, stopIndex + 1);

        const fieldPositions = fragments.filter(
            (f) =>
                f.getFieldPosition() !== null &&
                !excludeStates.includes(f.associatedState)
        );

        const [lastFieldPosition] = fieldPositions.slice(-1);

        return lastFieldPosition && lastFieldPosition.getFieldPosition();
    }

    setDrive(drive) {
        this.driveId = drive.id;
    }

    isInitialStep() {
        return this.getState() === FootballState.AddPlay;
    }

    canBeFinalized(fromState) {
        return !!this.getStateTransitions(fromState).find(
            (t) => t.name === FootballTransition.Finalize
        );
    }

    getContextSnapshot() {
        return this.contextSnapshot;
    }

    setContextSnapshot(contextSnapshot) {
        this.contextSnapshot = contextSnapshot;
    }

    getYardsGained(
        fieldDirectionMultiplier,
        startSpot,
        excludeStates = [],
        stopAtStates = []
    ) {
        let yardsGained = 0;

        const contextSnapshot = this.getContextSnapshot();
        if (contextSnapshot) {
            const endFieldPosition = this.getEndFieldPosition(
                excludeStates,
                stopAtStates
            );

            if (endFieldPosition != null) {
                yardsGained =
                    (endFieldPosition -
                        (startSpot || contextSnapshot.fieldPosition)) *
                    fieldDirectionMultiplier;
            }
        }

        return yardsGained;
    }

    getPreviousPossession(play, fragment) {
        const previousFragment = play.getPreviousFragment(fragment);
        if (!previousFragment) {
            return null;
        }

        if (!previousFragment.getFragmentTeamId()) {
            return this.getPreviousPossession(play, previousFragment);
        }

        return previousFragment.getFragmentTeamId();
    }

    resolveFragmentTeamId(play, fragment, gameContext) {
        let teamId =
            this.getPreviousPossession(play, fragment) ||
            gameContext?.getPossessionTeamId() ||
            play.contextSnapshot.getPossessionTeamId();

        return teamId;
    }

    resolveStepTeamId(rd, fragmentTeamId, gameContext) {
        if (rd.appliesToOpponent?.()) {
            return gameContext.getOpponent(fragmentTeamId);
        }
        return fragmentTeamId;
    }

    getEndContext(gameContext) {
        if (!this.endContext) {
            //TODO: make sure this.endContext is not saved in the play
            this.endContext = new FootballPlayEndContextSnapshot(
                this.contextSnapshot
            );
            const { gameConfig } = gameContext;

            Object.assign(this.endContext, {
                gameConfig
            });

            this.fragments.forEach((f) =>
                f.updateSummaryContext(this.endContext, this)
            );
        }

        return this.endContext;
    }

    isEditable() {
        return this.fragments?.some((f) =>
            f.requiredData?.some((rd) => rd.isEditable)
        );
    }

    shouldPromptSkipKickoff(gameContext) {
        if (!this.historyContains(FootballState.PatTry)) {
            const { promptToSkipKickoff } = gameContext.gameConfig.general;
            if (this.historyContains(FootballState.FieldGoalGood)) {
                return promptToSkipKickoff?.some(
                    (ks) => ks === KickoffSkip.FieldGoal.code
                );
            } else if (this.historyContains(FootballState.Safety)) {
                return promptToSkipKickoff?.some(
                    (ks) => ks === KickoffSkip.Safety.code
                );
            }
        }

        return false;
    }

    /**
     * True when this play carries an operator-collected clock (its end-of-play
     * fragment is a CollectDriveTime), as opposed to having inherited the clock
     * from a prior play. Such times are authoritative and bound time edits.
     */
    hasCollectedTime() {
        return (this.fragments || []).some(
            (f) => f instanceof CollectDriveTime
        );
    }

    shouldCollectTime(gameConfig) {
        const { currentPeriod } = this.contextSnapshot;
        const { otTimeControl, periodsInRegulation, fieldLength } =
            gameConfig?.general || DefaultFootballConfig.general;
        const disabledTimeOT =
            currentPeriod > periodsInRegulation && !otTimeControl;

        if (disabledTimeOT) return false;
        if (this.historyContains(FootballState.GameControl)) return false;
        if (this.historyContains(FootballState.Penalty)) return false;
        if (this.historyContains(FootballState.PatTry)) return false;

        return this._isTimeCollectedPlay(gameConfig);
    }

    _isTimeCollectedPlay(gameConfig) {
        const { fieldLength } =
            gameConfig?.general || DefaultFootballConfig.general;

        // Kickoff plays
        if (this.historyContains(FootballState.KickOff)) return true;

        // Explicit scoring states in FSM history
        if (this.historyContains(FootballState.FieldGoalGood)) return true;
        if (this.historyContains(FootballState.Safety)) return true;

        // TD heuristic: ball at end zone boundary
        const endPos = this.getEndFieldPosition();
        if (endPos != null && (endPos <= 0 || endPos >= fieldLength)) {
            return true;
        }

        // A play that ends the drive via a change of possession also sets the
        // drive boundary clock (the next drive starts at this play's time), so
        // it must collect time too — otherwise the ending drive gets no
        // elapsed time (TOP 0) and there is nothing to infer the end from.
        if (this._endsDriveByChangeOfPossession(gameConfig)) {
            return true;
        }

        return false;
    }

    /**
     * True when this play hands the ball to the other team (and so ends the
     * current drive): an interception, a punt, a lost fumble, or a
     * turnover on downs. Detected from play data available before finalize.
     */
    _endsDriveByChangeOfPossession(gameConfig) {
        if (this.historyContains(FootballState.PassIntercepted)) return true;
        if (this.historyContains(FootballState.Punt)) return true;
        if (this._isLostFumble()) return true;
        if (this._isTurnoverOnDowns(gameConfig)) return true;

        return false;
    }

    _isLostFumble() {
        const snapshot = this.contextSnapshot;
        const possessingTeamId = snapshot?.getPossessionTeamId?.();
        if (!possessingTeamId) return false;

        // A fumble recovery fragment carries the recovering player; if their
        // team differs from the team that had the ball, possession changed.
        return this.fragments.some(
            (f) =>
                f.recoveredBy?.teamId &&
                f.recoveredBy.teamId !== possessingTeamId
        );
    }

    _isTurnoverOnDowns(gameConfig) {
        const snapshot = this.contextSnapshot;
        const { downs } = gameConfig?.general || DefaultFootballConfig.general;
        if (!snapshot || snapshot.down !== downs) return false;

        // Scrimmage kicks on the last down are their own boundary type
        // (handled above / by their own states), not a turnover on downs.
        if (this.historyContains(FootballState.Punt)) return false;
        if (this.historyContains(FootballState.FieldGoal)) return false;

        const endPos = this.getEndFieldPosition();
        const { fieldPosition, firstDownAt } = snapshot;
        if (endPos == null || fieldPosition == null || firstDownAt == null) {
            return false;
        }

        // Converted when the ball reached the line to gain in the direction of
        // travel (firstDownAt relative to the snap spot). Direction-agnostic.
        const direction = Math.sign(firstDownAt - fieldPosition);
        const converted =
            direction === 0
                ? endPos === firstDownAt
                : (endPos - firstDownAt) * direction >= 0;

        return !converted;
    }

    /**
     * Retrieve the state required for a replacement play insertion to be successfully validated
     * @returns {FootballState}
     */
    getRequiredReplacementState() {
        const stateHistory = this.getHistory().map(({ name }) => name);
        const addPlayIdx = stateHistory.findIndex(
            (state) => state === FootballState.AddPlay
        );

        return stateHistory.find(
            (state, idx) =>
                idx > addPlayIdx &&
                ![FootballState.Kick, FootballState.GameControl].some(
                    (s) => s === state
                )
        );
    }
}

Object.values(FootballTransition).forEach((transition) => {
    FootballPlay.prototype[transition] = function (fragment, gameContext) {
        return this.doTransition(transition, fragment, gameContext);
    };
});
